import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Chip,
  Collapse,
  Tooltip,
  TableSortLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  MedicalServices as MedicalIcon,
  Clear as ClearIcon,
  AttachFile as AttachFileIcon,
  RemoveCircle as RemoveIcon,
  Pets as PetsIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Shuffle as ShuffleIcon,
  Public as PublicIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import DateField from './DateField';
import HelpButton from './HelpButton';
import { formatDate } from '../utils/dateUtils';
import { translateCostClass } from '../utils/translationUtils';
import CostClassHistoryPanel from './CostClassHistoryPanel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const wineNames = [
  'Nebbiolo', 'Sangiovese', 'Barbera', 'Montepulciano', 'Primitivo',
  "Nero d'Avola", 'Aglianico', 'Corvina', 'Dolcetto', 'Lambrusco',
  'Moscato', 'Trebbiano', 'Vermentino', 'Garganega', 'Prosecco',
  'Pinot Grigio', 'Vernaccia', 'Verdicchio', 'Malvasia', 'Greco',
  'Barolo', 'Barbaresco', 'Brunello', 'Chianti', 'Amarone',
  'Valpolicella', 'Taurasi', 'Sagrantino', 'Franciacorta', 'Gavi',
  'Cabernet Sauvignon', 'Merlot', 'Pinot Noir', 'Chardonnay', 'Syrah',
  'Grenache', 'Sauvignon Blanc', 'Viognier', 'Chenin Blanc', 'Riesling',
  'Chablis', 'Meursault', 'Pommard', 'Volnay', 'Beaune',
  'Châteauneuf-du-Pape', 'Hermitage', 'Côte-Rôtie', 'Gigondas',
  'Sancerre', 'Pouilly-Fumé', 'Chinon', 'Vouvray', 'Champagne',
  'Tempranillo', 'Garnacha', 'Albariño', 'Verdejo', 'Rioja',
];

const suggestCatName = () => wineNames[Math.floor(Math.random() * wineNames.length)];

interface Cat {
  id: number;
  chip_id?: string;
  name?: string;
  sex?: string;
  age?: string;
  breed?: string;
  color?: string;
  arrival_date?: string;
  departure_date?: string;
  municipality?: string;
  source?: string;
  address_where_found?: string;
  initial_health_status?: string;
  fiv?: string;
  felv?: string;
  cost_class?: string;
  temporary_placement?: string;
  area?: string;
  sterilized?: string;
  notes?: string;
  photo_file_path?: string;
  referente_di_colonia?: string;
}

interface Column {
  column_name: string;
  data_type: string;
}

interface FilterCriteria {
  id: string;
  field: string;
  value: string;
}

const CatsList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [cats, setCats] = useState<Cat[]>([]);
  const [filteredCats, setFilteredCats] = useState<Cat[]>([]);
  const [loading, setLoading] = useState(true);
  const [catsColumns, setCatsColumns] = useState<Column[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);

  // Selected cat for detail panel
  const [selectedCat, setSelectedCat] = useState<Cat | null>(null);

  // Filter state - multi-field with AND logic
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [currentFilterField, setCurrentFilterField] = useState<string>('');
  const [currentFilterValue, setCurrentFilterValue] = useState<string>('');

  // Edit cat dialog state
  const [editCatOpen, setEditCatOpen] = useState(false);
  const [catFormData, setCatFormData] = useState<any>({});
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);

  // Add event dialog state
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [eventFormData, setEventFormData] = useState<any>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Remove cat dialog state
  const [removeCatOpen, setRemoveCatOpen] = useState(false);
  const [toExternalOpen, setToExternalOpen] = useState(false);
  const [toExternalDate, setToExternalDate] = useState('');
  const [toExternalSaving, setToExternalSaving] = useState(false);
  const [removeEventType, setRemoveEventType] = useState<'Death' | 'Adoption' | 'Freed' | 'Just Remove' | ''>('');
  const [removeEventDate, setRemoveEventDate] = useState<string>('');
  const [removeNotes, setRemoveNotes] = useState<string>('');
  const [deathCertificateFile, setDeathCertificateFile] = useState<File | null>(null);
  const [adoptionFormFile, setAdoptionFormFile] = useState<File | null>(null);

  // Message state
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Add Cat dialog state
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [newCatFormData, setNewCatFormData] = useState<any>(() => {
    const d = new Date();
    return { arrival_date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` };
  });
  const [newCatPhotoFile, setNewCatPhotoFile] = useState<File | null>(null);
  const [newCatPhotoPreview, setNewCatPhotoPreview] = useState<string>('');
  const [newCatMissingFields, setNewCatMissingFields] = useState<Set<string>>(new Set());

  // Filter panel collapse state
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // Column widths for resizable table
  const [colWidths, setColWidths] = useState<Record<string, number>>({
    id: 50, chip_id: 130, name: 120, sex: 48,
    color: 80, municipality: 120, fiv: 52, felv: 52, sterilized: 60, cost_class: 110,
  });
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (col: string) => {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortedCats = useMemo(() => {
    if (!sortCol) return filteredCats;
    return [...filteredCats].sort((a, b) => {
      const ar = (a as any)[sortCol] ?? '';
      const br = (b as any)[sortCol] ?? '';
      const an = Number(ar), bn = Number(br);
      if (!isNaN(an) && !isNaN(bn) && ar !== '' && br !== '') {
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      const av = String(ar).toLowerCase();
      const bv = String(br).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filteredCats, sortCol, sortDir]);
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);

  const handleResizeMouseDown = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { col, startX: e.clientX, startWidth: colWidths[col] };

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const newWidth = Math.max(40, resizingRef.current.startWidth + ev.clientX - resizingRef.current.startX);
      setColWidths(prev => ({ ...prev, [resizingRef.current!.col]: newWidth }));
    };
    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  // Add Event without selected cat state
  const [addEventFromButtonOpen, setAddEventFromButtonOpen] = useState(false);
  const [eventCatId, setEventCatId] = useState<number | ''>('');

  // Initialize filters from URL params on mount
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      // Parse filter param format: "field:value" or "field:value,field2:value2"
      const filterParts = filterParam.split(',');
      const initialFilters: FilterCriteria[] = filterParts.map((part: string, index: number) => {
        const [field, value] = part.split(':');
        return {
          id: `url-${index}-${Date.now()}`,
          field: field || '',
          value: value || '',
        };
      }).filter((f: FilterCriteria) => f.field && f.value);

      if (initialFilters.length > 0) {
        setFilters(initialFilters);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    loadCats();
    loadCatsColumns();
    loadMunicipalities();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [cats, filters]);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return null;

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post(`/api/upload/invoice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.file_path;
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `File upload failed: ${error.response?.data?.detail || error.message}`,
      });
      return null;
    }
  };

  const loadCats = async () => {
    try {
      setLoading(true);
      // Phase 4: Add status=active filter for unified cats table.
      // external=false keeps externally-managed (non-shelter) cats out of this list.
      const response = await api.get(`/api/tables/public/cats/data?status=active&external=false&limit=1000`);
      const columns: string[] = response.data.columns;
      const catsData = response.data.rows.map((row: any[]) => {
        const cat: any = {};
        columns.forEach((col, i) => {
          cat[col] = row[i];
        });
        return cat as Cat;
      });
      setCats(catsData);
      setFilteredCats(catsData);
    } catch (error) {
      console.error('Error loading cats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCatsColumns = async () => {
    try {
      const response = await api.get(`/api/tables/public/cats/columns`);
      setCatsColumns(response.data);
    } catch (error) {
      console.error('Error loading cats columns:', error);
    }
  };

  const loadMunicipalities = async () => {
    try {
      const response = await api.get(`/api/tables/public/municipalities/data?limit=1000`);
      const municipalitiesData = response.data.rows.map((row: any[]) => row[0]);
      setMunicipalities(municipalitiesData.sort());
    } catch (error) {
      console.error('Error loading municipalities:', error);
    }
  };

  // Add Cat handlers
  const handleNewCatPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setNewCatPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewCatPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNewCatPhotoUpload = async () => {
    if (!newCatPhotoFile) return null;
    try {
      const formData = new FormData();
      formData.append('file', newCatPhotoFile);
      const response = await api.post(`/api/upload/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.file_path;
    } catch (error: any) {
      setMessage({ type: 'error', text: `Photo upload failed: ${error.response?.data?.detail || error.message}` });
      return null;
    }
  };

  const handleAddCat = async () => {
    // Validate required fields
    const requiredFields: { key: string; label: string }[] = [
      { key: 'name', label: 'Name' },
      { key: 'color', label: 'Color' },
      { key: 'source', label: 'Source' },
      { key: 'municipality', label: 'Municipality' },
      { key: 'address_where_found', label: 'Address Where Found' },
      { key: 'arrival_date', label: 'Arrival Date' },
    ];
    const missing = requiredFields.filter(f => !newCatFormData[f.key]);
    if (missing.length > 0) {
      setNewCatMissingFields(new Set(missing.map(f => f.key)));
      setMessage({ type: 'error', text: `Please fill in: ${missing.map(f => f.label).join(', ')}` });
      return;
    }
    setNewCatMissingFields(new Set());

    try {
      let photoPath = null;
      if (newCatPhotoFile) {
        photoPath = await handleNewCatPhotoUpload();
        if (!photoPath) return;
      }

      const dataToSubmit = {
        ...newCatFormData,
        ...(photoPath && { photo_file_path: photoPath })
      };
      if (dataToSubmit.sterilized === 'U') dataToSubmit.sterilized = null;
      // Sex is optional: send null (not '') so the cats_sex_check constraint passes
      if (!dataToSubmit.sex) dataToSubmit.sex = null;

      const catResponse = await api.post(`/api/records`, {
        table: 'cats',
        schema: 'public',
        data: dataToSubmit,
      });

      // Seed the cost class history so expense reports calculate correctly
      const newCatId = catResponse.data?.data?.id;
      if (newCatId) {
        await api.post(`${API_URL}/api/cost-class-history/cost_class_history/cat_id/${newCatId}/upsert`, {
          cost_class: dataToSubmit.cost_class || 'Ordinary',
          effective_from: dataToSubmit.arrival_date,
          notes: 'Seeded from initial cost_class at migration',
        });
      }

      setMessage({ type: 'success', text: 'Cat added successfully!' });
      setNewCatFormData({ arrival_date: getTodayDate() });
      setNewCatMissingFields(new Set());
      setNewCatPhotoFile(null);
      setNewCatPhotoPreview('');
      setAddCatOpen(false);
      loadCats();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    }
  };

  // Add Event from button (without pre-selected cat)
  const handleAddEventFromButton = async () => {
    if (!eventCatId) {
      setMessage({ type: 'error', text: 'Please select a cat' });
      return;
    }

    try {
      // Validate one-time events
      const eventType = eventFormData.event_type;
      if ((eventType === 'Sterilization' || eventType === 'Death') && eventCatId) {
        const checkResponse = await api.post(`/api/search`, {
          table: 'medical',
          schema: 'public',
          search_column: 'cat_id',
          search_value: eventCatId.toString(),
          limit: 100,
        });

        const existingEvents = checkResponse.data.rows.map((row: any[]) => {
          const result: any = {};
          checkResponse.data.columns.forEach((col: string, i: number) => {
            result[col] = row[i];
          });
          return result;
        });

        if (eventType === 'Sterilization' && existingEvents.some((e: any) => e.event_type === 'Sterilization')) {
          setMessage({ type: 'error', text: 'This cat already has a Sterilization event.' });
          return;
        }
        if (eventType === 'Death' && existingEvents.some((e: any) => e.event_type === 'Death')) {
          setMessage({ type: 'error', text: 'This cat already has a Death event.' });
          return;
        }
      }

      let filePath = null;
      if (selectedFile) {
        filePath = await handleFileUpload();
        if (!filePath) return;
      }

      const eventData = { ...eventFormData, cat_id: eventCatId };
      if (filePath) eventData.invoice_file_path = filePath;

      await api.post(`/api/records`, {
        table: 'medical',
        schema: 'public',
        data: eventData,
      });

      setMessage({ type: 'success', text: 'Medical event added successfully!' });
      setEventFormData({ event_date: getTodayDate() });
      setSelectedFile(null);
      setEventCatId('');
      setAddEventFromButtonOpen(false);
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    }
  };

  const applyFilter = () => {
    if (filters.length === 0) {
      setFilteredCats(cats);
      return;
    }

    const filtered = cats.filter((cat) => {
      // ALL filters must match (AND logic)
      return filters.every((filter: FilterCriteria) => {
        const fieldValue = cat[filter.field as keyof Cat];
        if (fieldValue === null || fieldValue === undefined) return false;
        return String(fieldValue).toLowerCase().includes(filter.value.toLowerCase());
      });
    });
    setFilteredCats(filtered);
  };

  const addFilter = () => {
    if (!currentFilterField || !currentFilterValue) return;
    const newFilter: FilterCriteria = {
      id: Date.now().toString(),
      field: currentFilterField,
      value: currentFilterValue,
    };
    setFilters([...filters, newFilter]);
    setCurrentFilterField('');
    setCurrentFilterValue('');
  };

  const removeFilter = (filterId: string) => {
    setFilters(filters.filter((f: FilterCriteria) => f.id !== filterId));
  };

  const clearAllFilters = () => {
    setFilters([]);
    setCurrentFilterField('');
    setCurrentFilterValue('');
  };

  const getFieldLabel = (fieldName: string): string => {
    const labels: Record<string, string> = {
      chip_id: 'Chip ID',
      name: 'Name',
      sex: 'Sex',
      age: 'Age',
      breed: 'Breed',
      municipality: 'Municipality',
      address_where_found: 'Address',
      source: 'Source',
      referente_di_colonia: 'Referente',
      sterilized: 'Sterilized',
      fiv: 'FIV',
      felv: 'FeLV',
      area: 'Area',
      temporary_placement: 'Temp. Placement',
    };
    return labels[fieldName] || fieldName;
  };

  const handleRowClick = (cat: Cat) => {
    setSelectedCat(cat);
  };

  const handleEditCat = (catArg?: Cat) => {
    const c = catArg ?? selectedCat;
    if (!c) return;
    if (catArg) setSelectedCat(catArg);
    setCatFormData({
      chip_id: c.chip_id || '',
      name: c.name || '',
      sex: c.sex || '',
      age: c.age || '',
      breed: c.breed || '',
      color: c.color || '',
      source: c.source || '',
      municipality: c.municipality || '',
      arrival_date: c.arrival_date || '',
      departure_date: c.departure_date || '',
      address_where_found: c.address_where_found || '',
      initial_health_status: c.initial_health_status || '',
      fiv: c.fiv || '',
      felv: c.felv || '',
      sterilized: c.sterilized ?? 'U',
      cost_class: c.cost_class || 'Ordinary',
      temporary_placement: c.temporary_placement || '',
      area: c.area || '',
      notes: c.notes || '',
      photo_file_path: c.photo_file_path || '',
      referente_di_colonia: c.referente_di_colonia || '',
    });
    setEditPhotoFile(null);
    setEditCatOpen(true);
  };

  const handleUpdateCat = async () => {
    try {
      let updatedData = { ...catFormData };
      if (updatedData.sterilized === 'U') updatedData.sterilized = null;

      // Upload photo if a new one is selected
      if (editPhotoFile) {
        const formData = new FormData();
        formData.append('file', editPhotoFile);

        const uploadResponse = await api.post(`/api/upload/photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        updatedData.photo_file_path = uploadResponse.data.file_path;
      }

      await api.put(`/api/records`, {
        table: 'cats',
        schema: 'public',
        record_id: selectedCat?.id,
        data: updatedData,
      });

      // Upsert a cost-class history entry for today (insert or update if already exists)
      if (selectedCat && updatedData.cost_class && updatedData.cost_class !== selectedCat.cost_class) {
        const todayStr = new Date().toISOString().split('T')[0];
        try {
          await api.post(`${API_URL}/api/cost-class-history/cost_class_history/cat_id/${selectedCat.id}/upsert`, {
            cost_class: updatedData.cost_class,
            effective_from: todayStr,
          });
        } catch (e: any) { console.error('Cost class history upsert failed:', e?.response?.data || e?.message); }
      }

      setMessage({ type: 'success', text: 'Cat updated successfully!' });
      setEditCatOpen(false);
      setEditPhotoFile(null);
      setHistoryRefreshKey(k => k + 1);
      loadCats();
      // Update the selected cat with new data
      setSelectedCat({ ...selectedCat!, ...updatedData });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update cat';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleAddEventForCat = (catArg?: Cat) => {
    const c = catArg ?? selectedCat;
    if (!c) return;
    if (catArg) setSelectedCat(catArg);
    setEventFormData({ event_date: getTodayDate() });
    setSelectedFile(null);
    setAddEventOpen(true);
  };

  const handleAddEvent = async () => {
    try {
      const eventType = eventFormData.event_type;

      // Check for duplicate Sterilization event
      if (eventType === 'Sterilization') {
        const eventsResponse = await api.get(
          `/api/tables/public/medical/data?limit=1000`
        );
        const medCols: string[] = eventsResponse.data.columns;
        const catIdIdx = medCols.indexOf('cat_id');
        const eventTypeIdx = medCols.indexOf('event_type');
        const existingEvents = eventsResponse.data.rows.filter(
          (row: any[]) => row[catIdIdx] === selectedCat?.id
        );
        const hasSterilization = existingEvents.some((row: any[]) => row[eventTypeIdx] === 'Sterilization');
        if (hasSterilization) {
          setMessage({
            type: 'error',
            text: 'This cat is already sterilized. Each cat can only be sterilized once.',
          });
          return;
        }
      }

      // Upload file if selected
      let filePath = null;
      if (selectedFile) {
        filePath = await handleFileUpload();
        if (!filePath) {
          setMessage({ type: 'error', text: 'File upload failed. Please try again.' });
          return;
        }
      }

      await api.post(`/api/records`, {
        table: 'medical',
        schema: 'public',
        data: {
          cat_id: selectedCat?.id,
          ...eventFormData,
          ...(filePath && { invoice_file_path: filePath }),
        },
      });

      setMessage({ type: 'success', text: 'Medical event added successfully!' });
      setAddEventOpen(false);
      const savedEventData = { ...eventFormData };
      setEventFormData({ event_date: getTodayDate() });
      setSelectedFile(null);
      loadCats(); // Reload cats to update sterilized status
      // Update selectedCat immediately to reflect trigger-based changes
      if (selectedCat) {
        const updatedCat = { ...selectedCat };
        if (savedEventData.event_type === 'Sterilization') {
          updatedCat.sterilized = 'Y';
        }
        if (savedEventData.event_type === 'test_fiv_felv') {
          if (savedEventData.fiv_result) updatedCat.fiv = savedEventData.fiv_result;
          if (savedEventData.felv_result) updatedCat.felv = savedEventData.felv_result;
        }
        setSelectedCat(updatedCat);
      }
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add medical event' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleOpenRemoveCat = (catArg?: Cat) => {
    const c = catArg ?? selectedCat;
    if (!c) return;
    if (catArg) setSelectedCat(catArg);
    setRemoveEventType('');
    setRemoveEventDate(getTodayDate());
    setRemoveNotes('');
    setRemoveCatOpen(true);
  };

  const handleOpenToExternal = (catArg?: Cat) => {
    const c = catArg ?? selectedCat;
    if (!c) return;
    if (catArg) setSelectedCat(catArg);
    setToExternalDate(getTodayDate());
    setToExternalOpen(true);
  };

  const handleToExternal = async () => {
    if (!selectedCat) return;
    if (!toExternalDate) {
      setMessage({ type: 'error', text: t('external.effectiveDateRequired') });
      return;
    }
    setToExternalSaving(true);
    try {
      await api.post(`/api/cats/${selectedCat.id}/to-external`, { effective_date: toExternalDate });
      setToExternalOpen(false);
      setSelectedCat(null);
      setMessage({ type: 'success', text: t('external.movedToExternal') });
      loadCats();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || t('external.addFailed') });
    } finally {
      setToExternalSaving(false);
    }
  };

  const handleRemoveCat = async () => {
    if (!removeEventType) {
      setMessage({ type: 'error', text: 'Please select an event type' });
      return;
    }

    if (!removeEventDate) {
      setMessage({ type: 'error', text: 'Please provide an event date' });
      return;
    }

    try {
      // Use FormData to support file upload
      const formData = new FormData();
      formData.append('cat_id', String(selectedCat?.id));
      formData.append('event_type', removeEventType);
      formData.append('event_date', removeEventDate);
      if (removeNotes) {
        formData.append('notes', removeNotes);
      }
      if (deathCertificateFile && removeEventType === 'Death') {
        formData.append('death_certificate', deathCertificateFile);
      }
      if (adoptionFormFile && removeEventType === 'Adoption') {
        formData.append('adoption_form', adoptionFormFile);
      }

      await api.post(`/api/checkout-event-with-certificate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      let successMessage = '';
      if (removeEventType === 'Death') {
        successMessage = 'Cat moved to Angels successfully!';
      } else if (removeEventType === 'Adoption') {
        successMessage = 'Cat moved to Adopted successfully!';
      } else if (removeEventType === 'Freed') {
        successMessage = 'Cat moved to Freed successfully!';
      } else if (removeEventType === 'Just Remove') {
        successMessage = 'Cat has been removed from the database!';
      }

      setMessage({
        type: 'success',
        text: successMessage,
      });
      setRemoveCatOpen(false);
      setSelectedCat(null);
      setRemoveEventType('');
      setRemoveEventDate('');
      setRemoveNotes('');
      setDeathCertificateFile(null);
      setAdoptionFormFile(null);
      loadCats();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `Failed to remove cat: ${error.response?.data?.detail || error.message}`,
      });
    }
  };

  const renderFormField = (column: Column, formData: any, setFormData: any) => {
    if (column.column_name === 'id') return null;

    if (column.column_name === 'sex') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.sex')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('catFields.sex')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            <MenuItem value="M">M</MenuItem>
            <MenuItem value="F">F</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'age') {
      const ageValue = formData[column.column_name] || '';
      const parts = ageValue.split('/');
      const month = parts[0] || '';
      const year = parts[1] || '';
      return (
        <Box key={column.column_name} sx={{ display: 'flex', gap: 1, mt: 2, mb: 1 }}>
          <FormControl sx={{ width: '40%' }}>
            <InputLabel>{t('catFields.birthMonth')}</InputLabel>
            <Select
              value={month}
              label={t('catFields.birthMonth')}
              onChange={(e) => {
                const newMonth = e.target.value;
                setFormData({ ...formData, [column.column_name]: `${newMonth}/${year}` });
              }}
            >
              <MenuItem value=""><em>{t('common.month')}</em></MenuItem>
              {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={{ width: '60%' }}
            label={t('catFields.birthYear')}
            placeholder={t('common.year') || 'YYYY'}
            value={year}
            onChange={(e) => {
              const newYear = e.target.value.replace(/\D/g, '').slice(0, 4);
              setFormData({ ...formData, [column.column_name]: `${month}/${newYear}` });
            }}
            inputProps={{ maxLength: 4 }}
          />
        </Box>
      );
    }

    if (column.column_name === 'source') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.source')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('catFields.source')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            <MenuItem value="ASL">ASL</MenuItem>
            <MenuItem value="Private">{t('catSources.private')}</MenuItem>
            <MenuItem value="Municipality">{t('catSources.municipality')}</MenuItem>
            <MenuItem value="Referente di Colonia">{t('catSources.referenteDiColonia')}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'municipality') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.municipality')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('catFields.municipality')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            {municipalities.map((municipality: string) => (
              <MenuItem key={municipality} value={municipality}>
                {municipality}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'address_where_found') {
      return (
        <TextField
          key={column.column_name}
          fullWidth
          margin="normal"
          label={t('catFields.addressWhereFound')}
          value={formData[column.column_name] || ''}
          onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          multiline
          rows={2}
        />
      );
    }

    if (column.column_name === 'sterilized') {
      return null; // Sterilized is auto-updated by trigger
    }

    if (column.column_name === 'fiv') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.fiv')}</InputLabel>
          <Select
            value={formData[column.column_name] || 'Unknown'}
            label={t('catFields.fiv')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
            <MenuItem value="Y">Y</MenuItem>
            <MenuItem value="N">N</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'felv') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('catFields.felv')}</InputLabel>
          <Select
            value={formData[column.column_name] || 'Unknown'}
            label={t('catFields.felv')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
            <MenuItem value="Y">Y</MenuItem>
            <MenuItem value="N">N</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'photo_file_path') {
      return (
        <Box key={column.column_name} sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            {t('catFields.catPhoto')}
          </Typography>

          {/* Show existing photo */}
          {formData.photo_file_path && !editPhotoFile && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={`${API_URL}${formData.photo_file_path}`}
                alt="Cat photo"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 'auto',
                  borderRadius: 2,
                  border: '2px solid #667eea',
                  mb: 1,
                }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {t('cat.currentPhoto')}
              </Typography>
            </Box>
          )}

          {/* Show new photo preview */}
          {editPhotoFile && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={URL.createObjectURL(editPhotoFile)}
                alt="New cat photo"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 'auto',
                  borderRadius: 2,
                  border: '2px solid #4caf50',
                  mb: 1,
                }}
              />
              <Typography variant="caption" color="success.main" display="block" fontWeight="bold">
                {t('cat.newPhotoSelected')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(2)} KB)
              </Typography>
            </Box>
          )}

          {/* Upload button */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              component="label"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textTransform: 'none',
                fontWeight: 600,
                flexGrow: 1,
              }}
            >
              {editPhotoFile ? 'Change Photo' : formData.photo_file_path ? 'Replace Photo' : 'Upload Photo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setEditPhotoFile(e.target.files[0]);
                  }
                }}
              />
            </Button>
            {editPhotoFile && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setEditPhotoFile(null)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <TextField
        key={column.column_name}
        fullWidth
        margin="normal"
        label={column.column_name.replace(/_/g, ' ').toUpperCase()}
        type={column.data_type.includes('date') ? 'date' : column.data_type.includes('int') ? 'number' : 'text'}
        value={formData[column.column_name] || ''}
        onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
        InputLabelProps={column.data_type.includes('date') ? { shrink: true } : undefined}
      />
    );
  };

  // Detail field component for cleaner rendering
  const DetailField = ({ label, value }: { label: string; value: string | number | undefined }) => (
    <Box sx={{ mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
        {value || '-'}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
      {message && (
        <Alert
          severity={message.type}
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(102,126,234,0.15)',
          }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#667eea' }}>
          {t('lists.allCats')} ({filteredCats.length})
        </Typography>
        <HelpButton
          title={t('help.shelter.title')}
          intro={t('help.shelter.intro')}
          items={t('help.shelter.items', { returnObjects: true }) as string[]}
        />
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 1.5, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setNewCatFormData({ arrival_date: getTodayDate() });
      setNewCatMissingFields(new Set());
            setNewCatPhotoFile(null);
            setNewCatPhotoPreview('');
            setAddCatOpen(true);
          }}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
            }
          }}
        >
          {t('cat.addNewCat')}
        </Button>
      </Box>

      {/* Filter Section */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(102,126,234,0.12)',
        }}
      >
        <Box
          sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <FilterIcon sx={{ fontSize: 18, color: '#667eea' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            {t('lists.filterCats')}
          </Typography>
          {filters.length > 0 && !filtersExpanded && (
            <Chip label={filters.length} size="small" sx={{ height: 20, bgcolor: '#667eea', color: 'white', fontSize: '0.7rem' }} />
          )}
          {filtersExpanded ? <ExpandLessIcon sx={{ color: 'text.secondary', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />}
        </Box>
        <Collapse in={filtersExpanded}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('lists.field')}</InputLabel>
              <Select
                value={currentFilterField}
                label={t('lists.field')}
                onChange={(e) => setCurrentFilterField(e.target.value)}
              >
                <MenuItem value=""><em>{t('lists.selectFieldPlaceholder')}</em></MenuItem>
                <MenuItem value="chip_id">{t('cat.chipId')}</MenuItem>
                <MenuItem value="name">{t('cat.name')}</MenuItem>
                <MenuItem value="sex">{t('cat.sex')}</MenuItem>
                <MenuItem value="age">{t('cat.age')}</MenuItem>
                <MenuItem value="breed">{t('cat.breed')}</MenuItem>
                <MenuItem value="cost_class">{t('cat.costClass')}</MenuItem>
                <MenuItem value="municipality">{t('cat.municipality')}</MenuItem>
                <MenuItem value="address_where_found">{t('cat.addressWhereFound')}</MenuItem>
                <MenuItem value="source">{t('cat.source')}</MenuItem>
                <MenuItem value="referente_di_colonia">{t('cat.referenteDiColonia')}</MenuItem>
                <MenuItem value="sterilized">{t('cat.sterilized')}</MenuItem>
                <MenuItem value="fiv">FIV</MenuItem>
                <MenuItem value="felv">FeLV</MenuItem>
                <MenuItem value="area">{t('cat.area')}</MenuItem>
                <MenuItem value="temporary_placement">{t('cat.temporaryPlacement')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label={t('lists.value')}
              value={currentFilterValue}
              onChange={(e) => setCurrentFilterValue(e.target.value)}
              disabled={!currentFilterField}
              placeholder={t('lists.enterValue')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') addFilter();
              }}
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              onClick={addFilter}
              disabled={!currentFilterField || !currentFilterValue}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              {t('common.add')}
            </Button>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearAllFilters}
              disabled={filters.length === 0 && !currentFilterField && !currentFilterValue}
              startIcon={<ClearIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
        {/* Active Filters as Chips */}
        {filters.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5 }}>
              {t('lists.active')}
            </Typography>
            {filters.map((filter: FilterCriteria) => (
              <Chip
                key={filter.id}
                label={`${getFieldLabel(filter.field)}: ${filter.value}`}
                onDelete={() => removeFilter(filter.id)}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': { color: 'white' },
                  },
                }}
              />
            ))}
          </Box>
        )}
          </Box>
        </Collapse>
      </Paper>

      {/* Master-Detail Layout */}
      <Box sx={{ display: 'flex', gap: 1.5, height: 'calc(100vh - 310px)', minHeight: 300 }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flex: selectedCat ? '0 0 55%' : '1 1 100%',
          transition: 'flex 0.3s ease',
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(102,126,234,0.15)',
          overflow: 'auto',
        }}
      >
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 36 }} />
              {(['id','chip_id','name','sex','color','municipality','fiv','felv','sterilized','cost_class'] as const).map(col => (
                <col key={col} style={{ width: colWidths[col] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 600, fontSize: '0.75rem', padding: '6px 10px', position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>#</TableCell>
                {([
                  { col: 'id', label: 'ID' },
                  { col: 'chip_id', label: t('cat.chipId') },
                  { col: 'name', label: t('cat.name') },
                  { col: 'sex', label: t('cat.sex') },
                  { col: 'color', label: t('cat.color') },
                  { col: 'municipality', label: t('cat.municipality') },
                  { col: 'fiv', label: t('cat.fiv') },
                  { col: 'felv', label: t('cat.felv') },
                  { col: 'sterilized', label: t('cat.sterilized') },
                  { col: 'cost_class', label: t('cat.costClass') },
                ] as const).map(({ col, label }) => (
                  <TableCell key={col} onClick={() => handleSort(col)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortCol === col}
                      direction={sortCol === col ? sortDir : 'asc'}
                      sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.7) !important' } }}
                    >
                      {label}
                    </TableSortLabel>
                    <Box
                      onMouseDown={(e) => handleResizeMouseDown(col, e)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: 'absolute', right: 0, top: 0, bottom: 0, width: 5,
                        cursor: 'col-resize', zIndex: 1,
                        '&:hover': { background: 'rgba(255,255,255,0.35)' },
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography color="text.secondary">{t('common.loading')}</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredCats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography color="text.secondary">{t('lists.noCatsFound')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedCats.map((cat: Cat, index: number) => (
                  <TableRow
                    key={cat.id}
                    hover
                    onClick={() => handleRowClick(cat)}
                    sx={{
                      cursor: 'pointer',
                      '& td': { padding: '4px 8px', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 },
                      backgroundColor: selectedCat?.id === cat.id ? 'rgba(102,126,234,0.12)' : !cat.chip_id ? '#fffde7' : 'inherit',
                      borderLeft: selectedCat?.id === cat.id ? '3px solid #667eea' : '3px solid transparent',
                      '&:hover': {
                        backgroundColor: selectedCat?.id === cat.id ? 'rgba(102,126,234,0.18)' : 'rgba(0,0,0,0.04)',
                      }
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem', userSelect: 'none' }}>{index + 1}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{cat.id}</TableCell>
                    <TableCell>{cat.chip_id || <Typography component="span" sx={{ color: '#ed6c02', fontStyle: 'italic' }}>{t('cat.missing')}</Typography>}</TableCell>
                    <TableCell><strong>{cat.name || '-'}</strong></TableCell>
                    <TableCell>{cat.sex || '-'}</TableCell>
                    <TableCell>{cat.color || '-'}</TableCell>
                    <TableCell>{cat.municipality || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={cat.fiv === 'Y' ? 'Y' : cat.fiv === 'N' ? 'N' : '?'}
                        size="small"
                        color={cat.fiv === 'Y' ? 'error' : cat.fiv === 'N' ? 'success' : 'default'}
                        sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem', minWidth: 28 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cat.felv === 'Y' ? 'Y' : cat.felv === 'N' ? 'N' : '?'}
                        size="small"
                        color={cat.felv === 'Y' ? 'error' : cat.felv === 'N' ? 'success' : 'default'}
                        sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem', minWidth: 28 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cat.sterilized === 'Y' ? 'Y' : cat.sterilized === 'N' ? 'N' : '?'}
                        size="small"
                        color={cat.sterilized === 'Y' ? 'success' : cat.sterilized === 'N' ? 'error' : 'default'}
                        sx={{ fontWeight: 600, minWidth: 32 }}
                      />
                    </TableCell>
                    <TableCell>{translateCostClass(t, cat.cost_class)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
      </TableContainer>

      {/* Detail Panel (Right Side) */}
      {selectedCat && (
        <Paper
          elevation={0}
          sx={{
            flex: '0 0 42%',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(102,126,234,0.12)',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header with photo and name */}
            <Box sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #a78bfa 100%)',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}>
              {selectedCat.photo_file_path ? (
                <Box
                  component="img"
                  src={`${API_URL}${selectedCat.photo_file_path}`}
                  alt={selectedCat.name || 'Cat'}
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1.5,
                    objectFit: 'cover',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  }}
                />
              ) : (
                <Box sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                }}>
                  <PetsIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'white' }}>
                  {selectedCat.name || t('cat.unnamed')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  ID: {selectedCat.id} {`| Chip: ${selectedCat.chip_id || t('cat.missing')}`}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedCat(null)} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Action buttons */}
            <Box sx={{ p: 1, display: 'flex', gap: 0.5, borderBottom: '1px solid #eee' }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => handleEditCat()}
                size="small"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="contained"
                startIcon={<MedicalIcon />}
                onClick={() => handleAddEventForCat()}
                size="small"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('medical.medicalEvent')}
              </Button>
              <Button
                variant="contained"
                startIcon={<RemoveIcon />}
                onClick={() => handleOpenRemoveCat()}
                size="small"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('common.remove')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PublicIcon />}
                onClick={() => handleOpenToExternal()}
                size="small"
                sx={{
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('external.moveToExternal')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate(`/cat-record/${selectedCat.id}`)}
                size="small"
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('external.openRecord')}
              </Button>
            </Box>

            {/* Cat details */}
            <Box sx={{ p: 1.5, overflow: 'auto', flex: 1 }}>
              {/* Basic Info */}
              <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                {t('cat.basicInfo')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <DetailField label={t('cat.sex')} value={selectedCat.sex} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('cat.age')} value={selectedCat.age} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('cat.breed')} value={selectedCat.breed} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('cat.color')} value={selectedCat.color} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Location Info */}
              <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                {t('cat.locationSource')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <DetailField label={t('cat.municipality')} value={selectedCat.municipality} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('cat.source')} value={selectedCat.source} />
                </Grid>
                <Grid item xs={12}>
                  <DetailField label={t('cat.referenteDiColonia')} value={selectedCat.referente_di_colonia} />
                </Grid>
                <Grid item xs={12}>
                  <DetailField label={t('cat.addressWhereFound')} value={selectedCat.address_where_found} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('cat.temporaryPlacement')} value={selectedCat.temporary_placement} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('cat.area')} value={selectedCat.area} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Health Info */}
              <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                {t('cat.healthStatus')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <DetailField label={t('cat.initialHealthStatus')} value={selectedCat.initial_health_status} />
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {t('cat.fiv')}
                    </Typography>
                    <Chip
                      label={selectedCat.fiv === 'Y' ? 'Y' : selectedCat.fiv === 'N' ? 'N' : '?'}
                      size="small"
                      color={selectedCat.fiv === 'Y' ? 'error' : selectedCat.fiv === 'N' ? 'success' : 'default'}
                      sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {t('cat.felv')}
                    </Typography>
                    <Chip
                      label={selectedCat.felv === 'Y' ? 'Y' : selectedCat.felv === 'N' ? 'N' : '?'}
                      size="small"
                      color={selectedCat.felv === 'Y' ? 'error' : selectedCat.felv === 'N' ? 'success' : 'default'}
                      sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {t('cat.sterilized')}
                    </Typography>
                    <Chip
                      label={selectedCat.sterilized === 'Y' ? 'Y' : selectedCat.sterilized === 'N' ? 'N' : '?'}
                      size="small"
                      color={selectedCat.sterilized === 'Y' ? 'success' : selectedCat.sterilized === 'N' ? 'error' : 'default'}
                      sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Dates */}
              <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                {t('cat.dates')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <DetailField label={t('cat.arrivalDate')} value={formatDate(selectedCat.arrival_date)} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('cat.departureDate')} value={formatDate(selectedCat.departure_date)} />
                </Grid>
              </Grid>

              {/* Cost Class History */}
              <Divider sx={{ my: 1 }} />
              <CostClassHistoryPanel
                key={historyRefreshKey}
                entityId={selectedCat.id}
                entityTable="cats"
                onHistoryChanged={loadCats}
              />

              {/* Notes */}
              {selectedCat.notes && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                    {t('cat.notes')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                    {selectedCat.notes}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Paper>
      )}
      </Box>

      {/* Edit Cat Dialog */}
      <Dialog
        open={editCatOpen}
        onClose={() => {
          setEditCatOpen(false);
          setEditPhotoFile(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102,126,234,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 700 }}>
          {t('cat.editCat')}: {selectedCat?.name || selectedCat?.chip_id}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          {/* Identity Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.identity')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.chipId')} value={catFormData.chip_id || ''} onChange={(e) => setCatFormData({ ...catFormData, chip_id: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.name')} value={catFormData.name || ''} onChange={(e) => setCatFormData({ ...catFormData, name: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.sex')}</InputLabel>
                <Select value={catFormData.sex || ''} label={t('cat.sex')} onChange={(e) => setCatFormData({ ...catFormData, sex: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="F">F</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              {(() => {
                const ageValue = catFormData.age || '';
                const parts = ageValue.split('/');
                const month = parts[0] || '';
                const year = parts[1] || '';
                return (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl sx={{ width: '45%' }} size="small">
                      <InputLabel>{t('cat.birthMonth')}</InputLabel>
                      <Select
                        value={month}
                        label={t('cat.birthMonth')}
                        onChange={(e) => setCatFormData({ ...catFormData, age: `${e.target.value}/${year}` })}
                      >
                        <MenuItem value=""><em>{t('common.month')}</em></MenuItem>
                        {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      sx={{ width: '55%' }}
                      size="small"
                      label={t('cat.birthYear')}
                      placeholder={t('common.year') || 'YYYY'}
                      value={year}
                      onChange={(e) => {
                        const newYear = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setCatFormData({ ...catFormData, age: `${month}/${newYear}` });
                      }}
                      inputProps={{ maxLength: 4 }}
                    />
                  </Box>
                );
              })()}
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.breed')} value={catFormData.breed || ''} onChange={(e) => setCatFormData({ ...catFormData, breed: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.color')} value={catFormData.color || ''} onChange={(e) => setCatFormData({ ...catFormData, color: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Location & Source Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.locationSource')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.source')}</InputLabel>
                <Select value={catFormData.source || ''} label={t('cat.source')} onChange={(e) => setCatFormData({ ...catFormData, source: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="ASL">{t('cat.sources.asl')}</MenuItem>
                  <MenuItem value="Private">{t('cat.sources.private')}</MenuItem>
                  <MenuItem value="Municipality">{t('cat.sources.municipality')}</MenuItem>
                  <MenuItem value="Referente di Colonia">{t('cat.sources.referenteDiColonia')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.municipality')}</InputLabel>
                <Select value={catFormData.municipality || ''} label={t('cat.municipality')} onChange={(e) => setCatFormData({ ...catFormData, municipality: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {municipalities.map((m: string) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.referenteDiColonia')} value={catFormData.referente_di_colonia || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCatFormData({ ...catFormData, referente_di_colonia: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.addressWhereFound')} value={catFormData.address_where_found || ''} onChange={(e) => setCatFormData({ ...catFormData, address_where_found: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.temporaryPlacement')} value={catFormData.temporary_placement || ''} onChange={(e) => setCatFormData({ ...catFormData, temporary_placement: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.area')} value={catFormData.area || ''} onChange={(e) => setCatFormData({ ...catFormData, area: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Health Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.health')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.initialHealthStatus')} value={catFormData.initial_health_status || ''} onChange={(e) => setCatFormData({ ...catFormData, initial_health_status: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.sterilized')}</InputLabel>
                <Select value={catFormData.sterilized ?? 'U'} label={t('cat.sterilized')} onChange={(e) => setCatFormData({ ...catFormData, sterilized: e.target.value })}>
                  <MenuItem value="U">{t('common.unknown')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.fiv')}</InputLabel>
                <Select value={catFormData.fiv || 'Unknown'} label={t('cat.fiv')} onChange={(e) => setCatFormData({ ...catFormData, fiv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.felv')}</InputLabel>
                <Select value={catFormData.felv || 'Unknown'} label={t('cat.felv')} onChange={(e) => setCatFormData({ ...catFormData, felv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t('cat.costClass')}</InputLabel>
                <Select value={catFormData.cost_class || 'Ordinary'} label={t('cat.costClass')} onChange={(e) => setCatFormData({ ...catFormData, cost_class: e.target.value })}>
                  <MenuItem value="Ordinary">{t('cat.costClasses.ordinary')}</MenuItem>
                  <MenuItem value="Increased">{t('cat.costClasses.increased')}</MenuItem>
                  <MenuItem value="Increased Kitten">{t('cat.costClasses.increasedKitten')}</MenuItem>
                  <MenuItem value="Increased Sickroom">{t('cat.costClasses.increasedSickroom')}</MenuItem>
                  <MenuItem value="Other">{t('cat.costClasses.other')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Dates Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.dates')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <DateField label={t('cat.arrivalDate')} value={catFormData.arrival_date || ''} onChange={(v) => setCatFormData({ ...catFormData, arrival_date: v })} />
            </Grid>
            <Grid item xs={6}>
              <DateField label={t('cat.departureDate')} value={catFormData.departure_date || ''} onChange={(v) => setCatFormData({ ...catFormData, departure_date: v })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Notes & Photo Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.notesPhoto')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.notes')} value={catFormData.notes || ''} onChange={(e) => setCatFormData({ ...catFormData, notes: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              {/* Photo section */}
              {catFormData.photo_file_path && !editPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    component="img"
                    src={`${API_URL}${catFormData.photo_file_path}`}
                    alt="Cat photo"
                    sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #667eea', mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">{t('cat.currentPhoto')}</Typography>
                </Box>
              )}
              {editPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    component="img"
                    src={URL.createObjectURL(editPhotoFile)}
                    alt="New cat photo"
                    sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #4caf50', mb: 0.5 }}
                  />
                  <Typography variant="caption" color="success.main" display="block" fontWeight="bold">{t('cat.newPhotoSelected')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(2)} KB)</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" component="label" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none', fontWeight: 600, flexGrow: 1 }}>
                  {editPhotoFile ? t('cat.changePhoto') : catFormData.photo_file_path ? t('cat.replacePhoto') : t('cat.uploadPhoto')}
                  <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) setEditPhotoFile(e.target.files[0]); }} />
                </Button>
                {editPhotoFile && (
                  <Button variant="outlined" color="error" size="small" onClick={() => setEditPhotoFile(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('common.clear')}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditCatOpen(false);
              setEditPhotoFile(null);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpdateCat}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            {t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Medical Event Dialog */}
      <Dialog
        open={addEventOpen}
        onClose={() => {
          setAddEventOpen(false);
          setSelectedFile(null);
          setEventFormData({ event_date: getTodayDate() });
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102,126,234,0.25)',
          }
        }}
      >
        <DialogTitle>{t('medical.addMedicalEventFor')}: {selectedCat?.name || selectedCat?.chip_id}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('medical.eventType')} *</InputLabel>
            <Select
              value={eventFormData.event_type || ''}
              label={`${t('medical.eventType')} *`}
              onChange={(e) => {
                const newType = e.target.value;
                const updated = { ...eventFormData, event_type: newType };
                if (newType !== 'test_fiv_felv') {
                  delete updated.fiv_result;
                  delete updated.felv_result;
                }
                setEventFormData(updated);
              }}
              required
            >
              <MenuItem value="Vaccine">{t('medical.eventTypes.vaccine')}</MenuItem>
              <MenuItem value="Sterilization">{t('medical.eventTypes.sterilization')}</MenuItem>
              <MenuItem value="Treatment">{t('medical.eventTypes.treatment')}</MenuItem>
              <MenuItem value="Delivery">{t('medical.eventTypes.delivery')}</MenuItem>
              <MenuItem value="Surgery">{t('medical.eventTypes.surgery')}</MenuItem>
              <MenuItem value="Checkup">{t('medical.eventTypes.checkup')}</MenuItem>
              <MenuItem value="test_fiv_felv">{t('medical.eventTypes.testFivFelv')}</MenuItem>
              <MenuItem value="Travel Expenses">{t('medical.eventTypes.travelExpenses')}</MenuItem>
              <MenuItem value="Other">{t('medical.eventTypes.other')}</MenuItem>
            </Select>
          </FormControl>
          {eventFormData.event_type === 'test_fiv_felv' && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('medical.fivResult')} *</InputLabel>
                  <Select
                    value={eventFormData.fiv_result || ''}
                    label={`${t('medical.fivResult')} *`}
                    onChange={(e) => setEventFormData({ ...eventFormData, fiv_result: e.target.value })}
                  >
                    <MenuItem value="Y">{t('common.positive')} (Y)</MenuItem>
                    <MenuItem value="N">{t('common.negative')} (N)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('medical.felvResult')} *</InputLabel>
                  <Select
                    value={eventFormData.felv_result || ''}
                    label={`${t('medical.felvResult')} *`}
                    onChange={(e) => setEventFormData({ ...eventFormData, felv_result: e.target.value })}
                  >
                    <MenuItem value="Y">{t('common.positive')} (Y)</MenuItem>
                    <MenuItem value="N">{t('common.negative')} (N)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          <DateField label={t('medical.eventDate')} value={eventFormData.event_date || ''} onChange={(v) => setEventFormData({ ...eventFormData, event_date: v })} />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.cost')}
            type="text"
            inputMode="decimal"
            value={eventFormData.cost || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setEventFormData({ ...eventFormData, cost: v });
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.veterinaryName')}
            value={eventFormData.veterinary_name || ''}
            onChange={(e) =>
              setEventFormData({ ...eventFormData, veterinary_name: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('cat.notes')}
            multiline
            rows={3}
            value={eventFormData.notes || ''}
            onChange={(e) => setEventFormData({ ...eventFormData, notes: e.target.value })}
          />

          {/* Invoice Upload Section */}
          <Box sx={{ mt: 3, p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              {t('medical.invoiceUploadOptional')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {t('medical.uploadDocument')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<AttachFileIcon />}
                color="primary"
                sx={{ flexGrow: 1 }}
              >
                {t('common.browseFiles')}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                />
              </Button>
              {selectedFile && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setSelectedFile(null)}
                >
                  {t('common.clear')}
                </Button>
              )}
            </Box>

            {selectedFile && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="medium" color="primary">
                  {t('common.fileSelected')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('common.size')}: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}

            {!selectedFile && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                {t('common.noFileSelected')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddEventOpen(false);
              setSelectedFile(null);
              setEventFormData({ event_date: getTodayDate() });
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddEvent}
            variant="contained"
            disabled={!eventFormData.event_type || (eventFormData.event_type === 'test_fiv_felv' && (!eventFormData.fiv_result || !eventFormData.felv_result))}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(240,147,251,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)',
              },
              '&:disabled': {
                background: '#ccc',
                color: '#666',
              }
            }}
          >
            {t('medical.addEvent')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Cat Dialog */}
      <Dialog
        open={removeCatOpen}
        onClose={() => {
          setRemoveCatOpen(false);
          setRemoveEventType('');
          setRemoveEventDate(getTodayDate());
          setRemoveNotes('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(250,112,154,0.25)',
          }
        }}
      >
        <DialogTitle>
          {t('checkout.removeCat')}: {selectedCat?.name || selectedCat?.chip_id || t('common.unknown')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('checkout.removeDescription')}
          </Typography>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>{t('medical.eventType')} *</InputLabel>
            <Select
              value={removeEventType}
              label={`${t('medical.eventType')} *`}
              onChange={(e) => setRemoveEventType(e.target.value as 'Death' | 'Adoption' | 'Freed' | 'Just Remove')}
            >
              <MenuItem value="Death">{t('checkout.death')}</MenuItem>
              <MenuItem value="Adoption">{t('checkout.adoption')}</MenuItem>
              <MenuItem value="Freed">{t('checkout.freed')}</MenuItem>
              <MenuItem value="Just Remove">{t('checkout.justRemove')}</MenuItem>
            </Select>
          </FormControl>

          <DateField label={`${t('medical.eventDate')} *`} value={removeEventDate} onChange={(v) => setRemoveEventDate(v)} required />

          <TextField
            fullWidth
            margin="normal"
            label={t('cat.notes')}
            multiline
            rows={3}
            value={removeNotes}
            onChange={(e) => setRemoveNotes(e.target.value)}
            placeholder={t('common.optional')}
          />

          {removeEventType === 'Death' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('checkout.deathCertificate')}
              </Typography>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeathCertificateFile(e.target.files?.[0] || null)}
                style={{ marginTop: '8px' }}
              />
              {deathCertificateFile && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('common.fileSelected')}: {deathCertificateFile.name}
                </Typography>
              )}
            </Box>
          )}

          {removeEventType === 'Adoption' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('checkout.adoptionForm')}
              </Typography>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdoptionFormFile(e.target.files?.[0] || null)}
                style={{ marginTop: '8px' }}
              />
              {adoptionFormFile && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('common.fileSelected')}: {adoptionFormFile.name}
                </Typography>
              )}
            </Box>
          )}

          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>{t('common.warning')}:</strong> {t('checkout.warningRemove')}
            {removeEventType === 'Death' && ` ${t('checkout.movedToAngels')}`}
            {removeEventType === 'Adoption' && ` ${t('checkout.movedToAdopted')}`}
            {removeEventType === 'Freed' && ` ${t('checkout.movedToFreed')}`}
            {removeEventType === 'Just Remove' && ` ${t('checkout.deletedPermanently')}`}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveCatOpen(false);
              setRemoveEventType('');
              setRemoveEventDate(getTodayDate());
              setRemoveNotes('');
              setDeathCertificateFile(null);
              setAdoptionFormFile(null);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRemoveCat}
            variant="contained"
            disabled={!removeEventType || !removeEventDate}
            sx={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(250,112,154,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e96089 0%, #edd02f 100%)',
              },
              '&:disabled': {
                background: '#ccc',
                color: '#666',
              }
            }}
          >
            {t('checkout.removeCat')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Cat Dialog */}
      <Dialog
        open={addCatOpen}
        onClose={() => {
          setAddCatOpen(false);
          setNewCatFormData({ arrival_date: getTodayDate() });
      setNewCatMissingFields(new Set());
          setNewCatPhotoFile(null);
          setNewCatPhotoPreview('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102,126,234,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 700 }}>
          {t('cat.addNewCat')}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          {/* Identity Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.identity')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.chipId')} value={newCatFormData.chip_id || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, chip_id: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label={t('cat.name')}
                required
                value={newCatFormData.name || ''}
                onChange={(e) => setNewCatFormData({ ...newCatFormData, name: e.target.value })}
                sx={newCatMissingFields.has('name') && !newCatFormData.name ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fffde7' } } : {}}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t('cat.suggestName')}>
                        <IconButton size="small" onClick={() => setNewCatFormData({ ...newCatFormData, name: suggestCatName() })}>
                          <ShuffleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.sex')}</InputLabel>
                <Select value={newCatFormData.sex || ''} label={t('cat.sex')} onChange={(e) => setNewCatFormData({ ...newCatFormData, sex: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="F">F</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              {(() => {
                const ageValue = newCatFormData.age || '';
                const parts = ageValue.split('/');
                const month = parts[0] || '';
                const year = parts[1] || '';
                return (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl sx={{ width: '45%' }} size="small">
                      <InputLabel>{t('cat.birthMonth')}</InputLabel>
                      <Select value={month} label={t('cat.birthMonth')} onChange={(e) => setNewCatFormData({ ...newCatFormData, age: `${e.target.value}/${year}` })}>
                        <MenuItem value=""><em>{t('common.month')}</em></MenuItem>
                        {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      sx={{ width: '55%', ...(newCatMissingFields.has('age') && !year ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fffde7' } } : {}) }} size="small"
                      label={t('cat.birthYear')} placeholder={t('common.year') || 'YYYY'}
                      value={year}
                      onChange={(e) => {
                        const newYear = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setNewCatFormData({ ...newCatFormData, age: `${month}/${newYear}` });
                      }}
                      inputProps={{ maxLength: 4 }}
                    />
                  </Box>
                );
              })()}
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.breed')} value={newCatFormData.breed || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, breed: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.color')} required value={newCatFormData.color || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, color: e.target.value })} sx={newCatMissingFields.has('color') && !newCatFormData.color ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fffde7' } } : {}} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Location & Source Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.locationSource')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" required sx={newCatMissingFields.has('source') && !newCatFormData.source ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fffde7' } } : {}}>
                <InputLabel>{t('cat.source')}</InputLabel>
                <Select value={newCatFormData.source || ''} label={t('cat.source')} onChange={(e) => setNewCatFormData({ ...newCatFormData, source: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="ASL">{t('cat.sources.asl')}</MenuItem>
                  <MenuItem value="Private">{t('cat.sources.private')}</MenuItem>
                  <MenuItem value="Municipality">{t('cat.sources.municipality')}</MenuItem>
                  <MenuItem value="Referente di Colonia">{t('cat.sources.referenteDiColonia')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" required sx={newCatMissingFields.has('municipality') && !newCatFormData.municipality ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fffde7' } } : {}}>
                <InputLabel>{t('cat.municipality')}</InputLabel>
                <Select value={newCatFormData.municipality || ''} label={t('cat.municipality')} onChange={(e) => setNewCatFormData({ ...newCatFormData, municipality: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {municipalities.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.referenteDiColonia')} value={newCatFormData.referente_di_colonia || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, referente_di_colonia: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.addressWhereFound')} required value={newCatFormData.address_where_found || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, address_where_found: e.target.value })} multiline rows={2} sx={newCatMissingFields.has('address_where_found') && !newCatFormData.address_where_found ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fffde7' } } : {}} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.temporaryPlacement')} value={newCatFormData.temporary_placement || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, temporary_placement: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.area')} value={newCatFormData.area || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, area: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Health Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.health')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.initialHealthStatus')} value={newCatFormData.initial_health_status || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, initial_health_status: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.sterilized')}</InputLabel>
                <Select value={newCatFormData.sterilized ?? 'U'} label={t('cat.sterilized')} onChange={(e) => setNewCatFormData({ ...newCatFormData, sterilized: e.target.value })}>
                  <MenuItem value="U">{t('common.unknown')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.fiv')}</InputLabel>
                <Select value={newCatFormData.fiv || 'Unknown'} label={t('cat.fiv')} onChange={(e) => setNewCatFormData({ ...newCatFormData, fiv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.felv')}</InputLabel>
                <Select value={newCatFormData.felv || 'Unknown'} label={t('cat.felv')} onChange={(e) => setNewCatFormData({ ...newCatFormData, felv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required sx={newCatMissingFields.has('cost_class') && !newCatFormData.cost_class ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fffde7' } } : {}}>
                <InputLabel>{t('cat.costClass')}</InputLabel>
                <Select value={newCatFormData.cost_class || 'Ordinary'} label={t('cat.costClass')} onChange={(e) => setNewCatFormData({ ...newCatFormData, cost_class: e.target.value })}>
                  <MenuItem value="Ordinary">{t('cat.costClasses.ordinary')}</MenuItem>
                  <MenuItem value="Increased">{t('cat.costClasses.increased')}</MenuItem>
                  <MenuItem value="Increased Kitten">{t('cat.costClasses.increasedKitten')}</MenuItem>
                  <MenuItem value="Increased Sickroom">{t('cat.costClasses.increasedSickroom')}</MenuItem>
                  <MenuItem value="Other">{t('cat.costClasses.other')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Dates Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.dates')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <DateField label={t('cat.arrivalDate')} value={newCatFormData.arrival_date || ''} onChange={(v) => setNewCatFormData({ ...newCatFormData, arrival_date: v })} required sx={newCatMissingFields.has('arrival_date') && !newCatFormData.arrival_date ? { '& .MuiOutlinedInput-root': { backgroundColor: '#fffde7' } } : {}} />
            </Grid>
            <Grid item xs={6}>
              <DateField label={t('cat.departureDate')} value={newCatFormData.departure_date || ''} onChange={(v) => setNewCatFormData({ ...newCatFormData, departure_date: v })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Notes & Photo Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.notesPhoto')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.notes')} value={newCatFormData.notes || ''} onChange={(e) => setNewCatFormData({ ...newCatFormData, notes: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              {newCatPhotoPreview && !newCatPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box component="img" src={newCatPhotoPreview} alt="Cat photo" sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #667eea', mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">{t('cat.currentPhoto')}</Typography>
                </Box>
              )}
              {newCatPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box component="img" src={newCatPhotoPreview} alt="New cat photo" sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #4caf50', mb: 0.5 }} />
                  <Typography variant="caption" color="success.main" display="block" fontWeight="bold">{t('cat.newPhotoSelected')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{newCatPhotoFile.name} ({(newCatPhotoFile.size / 1024).toFixed(2)} KB)</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" component="label" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none', fontWeight: 600, flexGrow: 1 }}>
                  {newCatPhotoFile ? t('cat.changePhoto') : t('cat.uploadPhoto')}
                  <input type="file" hidden accept="image/*" onChange={handleNewCatPhotoChange} />
                </Button>
                {newCatPhotoFile && (
                  <Button variant="outlined" color="error" size="small" onClick={() => { setNewCatPhotoFile(null); setNewCatPhotoPreview(''); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('common.clear')}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setAddCatOpen(false);
              setNewCatFormData({ arrival_date: getTodayDate() });
      setNewCatMissingFields(new Set());
              setNewCatPhotoFile(null);
              setNewCatPhotoPreview('');
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddCat}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
            }}
          >
            {t('cat.addCat')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Medical Event from Button Dialog */}
      <Dialog
        open={addEventFromButtonOpen}
        onClose={() => {
          setAddEventFromButtonOpen(false);
          setSelectedFile(null);
          setEventFormData({ event_date: getTodayDate() });
          setEventCatId('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(17,153,142,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', fontWeight: 700 }}>
          {t('medical.addMedicalEvent')}
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>{t('cat.selectCat')} *</InputLabel>
            <Select
              value={eventCatId}
              label={`${t('cat.selectCat')} *`}
              onChange={(e) => setEventCatId(e.target.value as number)}
            >
              <MenuItem value=""><em>{t('cat.selectCat')}...</em></MenuItem>
              {cats.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name || t('cat.unnamed')} (ID: {cat.id}, Chip: {cat.chip_id || t('cat.missing')})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>{t('medical.eventType')} *</InputLabel>
            <Select
              value={eventFormData.event_type || ''}
              label={`${t('medical.eventType')} *`}
              onChange={(e) => {
                const newType = e.target.value;
                const updated = { ...eventFormData, event_type: newType };
                if (newType !== 'test_fiv_felv') {
                  delete updated.fiv_result;
                  delete updated.felv_result;
                }
                setEventFormData(updated);
              }}
            >
              <MenuItem value="Vaccine">{t('medical.eventTypes.vaccine')}</MenuItem>
              <MenuItem value="Sterilization">{t('medical.eventTypes.sterilization')}</MenuItem>
              <MenuItem value="Treatment">{t('medical.eventTypes.treatment')}</MenuItem>
              <MenuItem value="Delivery">{t('medical.eventTypes.delivery')}</MenuItem>
              <MenuItem value="Surgery">{t('medical.eventTypes.surgery')}</MenuItem>
              <MenuItem value="Checkup">{t('medical.eventTypes.checkup')}</MenuItem>
              <MenuItem value="test_fiv_felv">{t('medical.eventTypes.testFivFelv')}</MenuItem>
              <MenuItem value="Travel Expenses">{t('medical.eventTypes.travelExpenses')}</MenuItem>
              <MenuItem value="Other">{t('medical.eventTypes.other')}</MenuItem>
            </Select>
          </FormControl>

          {eventFormData.event_type === 'test_fiv_felv' && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('medical.fivResult')} *</InputLabel>
                  <Select
                    value={eventFormData.fiv_result || ''}
                    label={`${t('medical.fivResult')} *`}
                    onChange={(e) => setEventFormData({ ...eventFormData, fiv_result: e.target.value })}
                  >
                    <MenuItem value="Y">{t('common.positive')} (Y)</MenuItem>
                    <MenuItem value="N">{t('common.negative')} (N)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('medical.felvResult')} *</InputLabel>
                  <Select
                    value={eventFormData.felv_result || ''}
                    label={`${t('medical.felvResult')} *`}
                    onChange={(e) => setEventFormData({ ...eventFormData, felv_result: e.target.value })}
                  >
                    <MenuItem value="Y">{t('common.positive')} (Y)</MenuItem>
                    <MenuItem value="N">{t('common.negative')} (N)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          <DateField label={t('medical.eventDate')} value={eventFormData.event_date || ''} onChange={(v) => setEventFormData({ ...eventFormData, event_date: v })} />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.cost')}
            type="text"
            inputMode="decimal"
            value={eventFormData.cost || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setEventFormData({ ...eventFormData, cost: v });
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.veterinaryName')}
            value={eventFormData.veterinary_name || ''}
            onChange={(e) => setEventFormData({ ...eventFormData, veterinary_name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('cat.notes')}
            multiline
            rows={3}
            value={eventFormData.notes || ''}
            onChange={(e) => setEventFormData({ ...eventFormData, notes: e.target.value })}
          />

          {/* Invoice Upload Section */}
          <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              {t('medical.invoiceUploadOptional')}
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<AttachFileIcon />}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                textTransform: 'none',
              }}
            >
              {t('cat.selectInvoice')}
              <input
                type="file"
                hidden
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileChange}
              />
            </Button>
            {selectedFile && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setAddEventFromButtonOpen(false);
              setSelectedFile(null);
              setEventFormData({ event_date: getTodayDate() });
              setEventCatId('');
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddEventFromButton}
            variant="contained"
            disabled={!eventCatId || !eventFormData.event_type || (eventFormData.event_type === 'test_fiv_felv' && (!eventFormData.fiv_result || !eventFormData.felv_result))}
            sx={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              '&:disabled': {
                background: '#ccc',
                color: '#666',
              }
            }}
          >
            {t('medical.addEvent')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move to external dialog */}
      <Dialog open={toExternalOpen} onClose={() => setToExternalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1.5, fontSize: '1rem' }}>{t('external.moveToExternal')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('external.moveToExternalConfirm', { name: selectedCat?.name || selectedCat?.chip_id || '' })}
          </Typography>
          <DateField label={t('external.effectiveDate')} value={toExternalDate} onChange={(v: string) => setToExternalDate(v)} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('external.moveToExternalHelp')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToExternalOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleToExternal} disabled={toExternalSaving}>
            {toExternalSaving ? <CircularProgress size={20} /> : t('external.moveToExternal')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CatsList;
